import { z } from 'zod';

import { BaseItemSchema } from '../common.js';
import { type PollingSchedule } from '../feed.js';
import { type Connection, ConnectionType } from './common.js';

export enum SftpSourceGeneration {
  Legacy = 'Legacy',
  Channel = 'Channel',
}

export enum SftpDataType {
  UnstructuredFile = 'UnstructuredFile',
  StructuredFile = 'StructuredFile',
}

export enum FeedStatus {
  Enabled = 'Enabled',
  Disabled = 'Disabled',
}

/**
 * Cloned from backend/resource-gateway/src/domains/connection-management/models/connectionChannel.ts.
 * Keep in sync with the backend schema.
 */
export enum ConnectionChannelState {
  Active = 'Active',
  Inactive = 'Inactive',
  SoftDeleted = 'SoftDeleted',
}

export const ConnectionChannelSchema = BaseItemSchema.extend({
  connectionId: z.uuid(),
  connectorChannelId: z.uuid(),
  organizationId: z.uuid(),
  state: z.enum(ConnectionChannelState),
  dataType: z.string().nullable(),
});

export type ConnectionChannel = z.infer<typeof ConnectionChannelSchema>;

/**
 * Cloned from backend/resource-gateway/src/domains/connection-management/models/connectionChannelSftp.ts.
 * SFTP extension table: no `id` column — one SFTP row per connection channel.
 */
export const ConnectionChannelSftpSchema = BaseItemSchema.omit({ id: true }).extend({
  connectionChannelId: z.uuid(),
  path: z.string().nullable(),
  documentType: z.string().nullable(),
  host: z.string(),
});

export type ConnectionChannelSftp = z.infer<typeof ConnectionChannelSftpSchema>;

/**
 * Cloned from backend/resource-gateway/src/domains/connection-management/models/connection.ts.
 * Keep in sync with the backend `Connection` interface.
 */
export enum ConnectionState {
  Active = 'Active',
  Inactive = 'Inactive',
  Error = 'Error',
}

export const ResourceGatewayConnectionSchema = BaseItemSchema.extend({
  connectorId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  organizationId: z.string(),
  state: z.enum(ConnectionState),
});

export type ResourceGatewayConnection = z.infer<typeof ResourceGatewayConnectionSchema>;

export const ConnectionSftpChannelSchema = z.object({
  connectionType: z.literal(ConnectionType.Sftp),
  sourceGeneration: z.literal(SftpSourceGeneration.Channel),
  connection: ResourceGatewayConnectionSchema,
  connectionChannel: ConnectionChannelSchema,
  connectionChannelSftp: ConnectionChannelSftpSchema,
});

export type ConnectionSftpChannel = z.infer<typeof ConnectionSftpChannelSchema>;

export type ConnectionSftpLegacy = Omit<Connection, 'connectionType' | 'lastUpdated'> & {
  connectionType: ConnectionType.Sftp;
  sourceGeneration: SftpSourceGeneration.Legacy;
  created: string;
  updated: string;
  status: FeedStatus;
  filePath: string;
  pollingSchedule?: PollingSchedule;
  practiceIds: string[];
  flowIds: string[];
};

export type ConnectionSftp = ConnectionSftpLegacy | ConnectionSftpChannel;

/**
 * Cloned from the `POST /connections/sftp` body schema in
 * backend/resource-gateway/src/domains/connection-management/interfaces/http/connections.ts.
 */
export const CreateSftpConnectionValuesSchema = z.object({
  organizationId: z.string(),
  name: z.string(),
  description: z.string().trim().optional(),
  dataType: z.enum(SftpDataType),
  documentType: z.string().trim().optional(),
  host: z.string(),
  path: z.string(),
});

export type CreateSftpConnectionValues = z.infer<typeof CreateSftpConnectionValuesSchema>;

export type CreateSftpConnectionResponse = ConnectionSftpChannel;

/**
 * Cloned from the `PATCH /connections/sftp/:id` body schema in
 * backend/resource-gateway/src/domains/connection-management/interfaces/http/connections.ts.
 * RG only accepts `Active`/`Inactive` transitions via this endpoint.
 */
export const UpdateSftpConnectionValuesSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().nullable().optional(),
  host: z.string().min(1).optional(),
  path: z.string().min(1).optional(),
  documentType: z.string().nullable().optional(),
  state: z.enum([ConnectionChannelState.Active, ConnectionChannelState.Inactive]).optional(),
});

export type UpdateSftpConnectionValues = z.infer<typeof UpdateSftpConnectionValuesSchema>;

export type UpdateSftpConnectionResponse = ConnectionSftpChannel;

/**
 * Raw shape returned by the resource-gateway SFTP endpoint. Identical to
 * `ConnectionSftpChannel` minus the discriminator fields the SDK stamps on.
 */
export const ResourceGatewaySftpConnectionRawResponseSchema = z.object({
  connection: ResourceGatewayConnectionSchema,
  connectionChannel: ConnectionChannelSchema,
  connectionChannelSftp: ConnectionChannelSftpSchema,
});

export type ResourceGatewaySftpConnectionRawResponse = z.infer<
  typeof ResourceGatewaySftpConnectionRawResponseSchema
>;
